/**
 * Globally shared UI strings used across the entire application.
 * Keep values in alphabetical order within each group.
 */
export const globalsEn = {
  actions: {
    addItem: 'Add',
    addPanel: 'Add Panel',
    andMore: 'and {count} more',
    apply: 'Apply',
    cancel: 'Cancel',
    clear: 'Clear',
    clearAll: 'Clear All',
    clearFilterSet: 'Clear Filters',
    clearFilters: 'Remove ({count}) Filter',
    confirm: 'Confirm',
    ok: 'OK',
    delete: 'Delete',
    deselectAll: 'Deselect All',
    download: 'Download',
    edit: 'Edit',
    export: 'Export',
    exporting: 'Exporting',
    exportToPdf: 'Export to PDF',
    help: 'Help',
    learnMore: 'Learn More',
    moreInfo: 'More Info',
    preview: 'Preview',
    print: 'Print',
    removeItem: 'Remove',
    reset: 'Reset',
    resetToDefault: 'Reset to Default',
    save: 'Save',
    savePanels: 'Save Panels',
    search: 'Search',
    selectAll: 'Select All',
    selected: '{count} selected | {count} selected',
    translations: 'Translations',
  },
  inquiryForm: {
    title: 'Promoted Listing Inquiry',
    close: 'Close',
    subtitle: 'Contact us if you need assistance or need more information on promoted listings or connections.',
    // HubSpot form ID (portal 4888868, region na1). Field labels live in the form
    // definition in HubSpot, so the French version is a separate form.
    formId: 'aaf76a16-cc23-41ff-b72e-8c74ece557c1',
  },
  calendar: {
    day: 'Day',
    formatHint: 'Format: \'MM/DD/YYYY\'',
    inputPlaceholder: 'MM/DD/YYYY',
    month: 'Month',
    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },
    next: 'Next',
    previous: 'Previous',
    selectDate: 'Select date',
    sync: 'Sync',
    syncTooltip: 'This will sync your calendar',
    today: 'Today',
    week: 'Week',
  },
  daysSelection: {
    title: 'Days Available',
    initials: {
      sunday: 'S',
      monday: 'M',
      tuesday: 'T',
      wednesday: 'W',
      thursday: 'T',
      friday: 'F',
      saturday: 'S',
    },
  },
  gallery: {
    actions: {
      back: 'Back',
      downloadFile: 'Download File',
      editPhoto: 'Edit Photo',
      translateFields: 'Translate fields',
      uploadFiles: 'Upload Files',
    },
    labels: {
      altText: 'Alt Text',
      current: 'Current',
      filename: 'Filename:',
      new: 'New',
      sourceHeight: 'Source Height:',
      sourceWidth: 'Source Width:',
    },
    titles: {
      editImage: 'Edit Image',
      gallery: 'Gallery',
      pagePhotos: 'Page Photos',
      uploadFiles: 'Upload Files',
    },
    tooltips: {
      disableAssign: 'Disable image/file/media from showing on listings and your connected website(s).',
      enableAssign: 'Enable image/file/media to show on listings and your connected website(s).',
    },
    upload: {
      clickOrDrag: 'Click or drag items here to upload',
      invalidFileType: 'Only images or PDF files can be uploaded!',
      uploadedFiles: 'Uploaded Files',
      uploading: 'Uploading...',
    },
    warnings: {
      pageRemovalPendingSave: 'Full photo removal will only occur when Page changes are saved!',
      photoRemovalTitle: 'Photo removal warning!',
    },
  },
  dataTable: {
    modalExport: {
      pickDateRange: 'Pick Date Range to Export',
    },
    exportCSV: 'Export CSV',
    noResultsFound: 'No Results Found',
  },
  dates: {
    range: {
      from: 'From',
      to: 'To',
    },
    validation: {
      startDateRange: 'From date',
      cannotBeBefore: 'cannot be before',
      endDateRange: 'To date',
    },
    endDate: 'End Date',
    lastModified: 'Last Modified',
    lastUpdated: 'Last Updated',
    today: 'Today',
    na: 'N/A',
    startDate: 'Start Date',
  },
  dateRangePicker: {
    selectDate: 'Select Date',
    customRange: 'Custom Range',
    lastDays: 'Last {count} Days',
    lastMonths: 'Last {count} Months',
    currentMonth: 'Current Month',
    previousMonth: 'Previous Month',
  },
  fileInput: {
    browseButton: 'Browse',
    noFileChosen: 'No file chosen',
    readableTypes: {
      image: 'JPG or PNG images',
      pdf: 'PDF files',
    },
    unsupportedFileType: 'Unsupported file type. Please upload only {type}.',
  },
  filters: {
    advancedFiltersLocked: 'Advanced filters are only available with full access.',
    all: 'All',
    allFilters: 'All Filters',
    applyFilters: 'Apply Filters',
    cities: 'Cities',
    filterReports: 'Filter Reports ({count})',
    filtersApplied: '[{count} filters applied] | [{count} filter applied] | [{count} filters applied]',
    filtersAppliedCount: '({count} Filter Applied) | ({count} Filters Applied)',
    maxCount: '(Max {max})',
    multipleValueRequired: 'At least one selection is required for filtering!',
    properties: 'Properties',
    resetFilters: 'Reset Filters',
    selectCities: 'Select Cities',
    selectedCount: '[{count} Selected]',
    selectProperties: 'Select Properties',
    selectPlaceholder: 'Select',
    selectStatus: 'Select Status',
    showMore: 'SHOW MORE',
    sidebarDefaultTitle: 'Filters',
    singleValueRequired: 'A value is required for filtering!',
    startEnd: 'Start & End',
    status: 'Status',
  },
  languageNames: {
    french: 'French',
    spanish: 'Spanish',
    chineseSimplified: 'Chinese Simplified',
    chineseTraditional: 'Chinese Traditional',
    fr: 'French',
    es: 'Spanish',
    zh: 'Chinese',
    de: 'German',
    pt: 'Portuguese',
    it: 'Italian',
    ja: 'Japanese',
    ko: 'Korean',
    ar: 'Arabic',
    ru: 'Russian',
    zhs: 'Chinese Simplified',
    zht: 'Chinese Traditional',
  },
  pagination: {
    of: 'of',
    showing: 'Showing',
  },
  panels: {
    addBuilding: 'Add Building',
    currentFile: 'currently:',
    currentLanguage: 'Current panel language:',
    noItemsInTranslateMode: 'Panel items must first be created in English, then will appear here to be translated.',
    notDisplayedOnWebsite: 'This field will not be displayed on your connected website.',
    panelLabel: 'Panel',
    selectBuilding: 'Select Building:',
    selectPage: 'Select Page...',
    select: 'Select',
    translationsDisabledTooltip: 'An English value must exist before this panel can be translated. Please save panel before adding translations.',
    errorNamesDifferent: 'Panel names must be different',
    errorSavingContent: 'Error saving custom content!',
    errorNeedsName: 'Custom content containers must at least have a name',
    errorSavingField: 'Error saving {groupName} - {fieldName}',
    errorFieldMissingValue: '{fieldName} is missing its required value',
  },
  permissionsAccess: {
    noPermissionToMakeChanges: 'You do not have permission to make changes',
    viewOnly: 'View Only',
  },
  permissionsBanners: {
    viewOnlyPage: 'This page is currently \'View-Only\'. You do not have the necessary permissions to edit this page, please contact your administrator for support.',
  },
  seoCard: {
    current: 'Current:',
    defaultTitleUrlMessage: 'By default the page title and page url will be created using the {alertType} title. You may customize either SEO component by entering customized URLs below.',
    metaDescription: 'Meta Description',
    metaDescriptionTooltip: 'The optimal length for a meta description is between 120-155 characters. Going over may result in words getting truncated.',
    metaImage: 'Meta Image',
    metaTitleTooltip: 'The optimal length for a meta title is between 50-60 characters. Going over may result in words getting truncated.',
    seoTitle: 'SEO Title',
    seoUrl: 'SEO Url',
    strengthAverage: 'Average',
    strengthOptimal: 'Optimal',
    strengthTooLong: 'Too Long',
    strengthTooShort: 'Too Short',
    title: 'SEO',
  },
  status: {
    active: 'Active',
    archived: 'Archived',
    busy: 'Busy',
    canceled: 'Cancelled',
    completed: 'Completed',
    declined: 'Declined',
    deleted: 'Deleted',
    delivered: 'Delivered',
    disabled: 'Disabled',
    enabled: 'Enabled',
    expired: 'Expired',
    failed: 'Failed',
    hidden: 'Hidden',
    inactive: 'Inactive',
    inProgress: 'In Progress',
    ivrAnswered: 'IVR Hangup',
    ivrTimeout: 'IVR Timeout',
    noAnswer: 'No Answer',
    notPublished: 'Not Published',
    published: 'Published',
    received: 'Received',
    requested: 'Requested',
    resolved: 'Resolved',
    scheduled: 'Scheduled',
    spam: 'Spam',
    undelivered: 'Undelivered',
    workScheduled: 'Work Scheduled',
  },
  tableTranslations: {
    searchPlaceholder: 'Search by {property}...',
    enterTranslation: 'Enter translation...',
    saveButton: 'Save translations',
    languageHeader: '{name} ({code})',
  },
  tipTap: {
    editContent: 'Edit Content',
  },
  toast: {
    successTitle: 'Success!',
    successMessage: 'Changes saved!',
    errorTitle: 'Error!',
    errorMessage: 'Something went wrong',
    genericError: 'An error occurred with this request. Please try again.',
    warningTitle: 'Warning!',
    warningMessage: 'Please note',
    youHaveSomeErrors: 'You have some errors! Please fix before saving.',
    progressTitle: 'Saving...',
    progressMessage: 'Changes are being saved...',
  },
  translations: {
    title: 'Translations',
    saveButton: 'Save Translations',
    searchLabel: 'Search',
    searchPlaceholder: 'Search by field name',
    unspecified: 'Unspecified',
    enterTranslation: 'Enter {lang} Translation...',
    toastSaving: 'Saving translations...',
    toastSavedTitle: 'Translations Saved!',
    toastSavedMessage: 'Your translations have been saved successfully.',
  },
  units: {
    months: 'months',
  },
  swal: {
    areYouSure: 'Are you sure?',
    areYouSureRemove: 'Are you sure you want to remove this?',
    continueWithoutSaving: 'Continue Without Saving',
    delete: 'Delete',
    saveChanges: 'Save Changes',
    success: 'Success!',
    operationSuccessful: 'Operation successful!',
  },
  unsavedChanges: {
    validTitle: 'You have unsaved changes!',
    validText: 'Do you want to save your changes before leaving?',
    validConfirm: 'Save changes',
    invalidTitle: 'You have unsaved changes with errors!',
    invalidText: 'You must fix the errors before saving. Do you want to leave without saving?',
    invalidConfirm: 'Stay & fix errors',
    continueWithoutSaving: 'Continue without saving',
  },
  validation: {
    alphaNumOnly: 'Please use letter or numbers only, no spaces.',
    dateFormat: 'Date requires format of MM/DD/YYYY.',
    dateInvalid: 'Invalid Date!',
    dateOutOfRange: 'Date is out of range.',
    emailInvalid: 'Email is invalid',
    emailsNotValid: 'Emails are not valid',
    embedCodeInvalid: 'The value is not a valid embed code.',
    fieldRequired: 'This field is required',
    formInvalid: 'Form is invalid.',
    numericInvalid: 'This field must be numeric.',
    numericOnly: 'Input must be numeric.',
    maxLength: 'This field may not be greater than {max} characters.',
    minLength: 'This field must be at least {min} characters.',
    maxValue: 'This field must be {max} or less.',
    minValue: 'This field must be {min} or more.',
    digits: 'This field must be numeric and exactly contain {length} digits.',
    regexInvalid: 'This field format is invalid.',
    phoneAltInvalid: 'Please enter a valid phone number.',
    phoneInputInvalid: 'Phone number input is not valid',
    phoneIntlInvalid: 'Please enter a valid phone number.',
    phoneNumberInvalid: 'Phone number is invalid.',
    phoneInvalid: 'Phone / Fax Number is invalid.',
    postalInvalid: 'Postal/Zip is not valid.',
    rentMin: 'Rent must be greater than 0',
    urlInvalid: 'Url is invalid',
    urlRegexInvalid: 'Property Website URL is not valid.',
  },
  imageUploader: {
    promptUploadNewImage: 'Upload New Image',
    supportedFileTypes: 'Supported file types: png, gif, jpg',
  },
  buildingStackSync: {
    unitAvailabilityLocked: 'Unit Availability editing is disabled by your sync settings.',
    unitStatusLocked: 'Unit Status editing is disabled by your sync settings.',
  },
  importer: {
    // `{'|'}` escapes the pipe: vue-i18n v9 reads a bare `|` as the plural-choice separator
    tooltip: 'Import ID: {importId} {\'|\'} Source: {source}',
    tooltipNoSource: 'Import ID: {importId}',
  },
  photoUpload: {
    deselectDisabledHint: 'Deselect all photos you would like to upload as disabled',
  },
  uploadProgress: {
    title: 'Upload in progress...',
    closeWarning: 'Please don\'t close this page until upload is complete',
    progressCount: 'Uploading: {current} / {total}',
    percentComplete: '{percent}% Complete',
  },
  multipleInputs: {
    placeholderDefault: 'Add value',
    duplicateTagText: 'Duplicate value',
  },
  formTagsSelector: {
    tooltipHint: 'Press the \'enter\' or comma key to create tags before saving.',
    frequentlyUsedTags: 'Frequently used tags:',
    translateAssignedTags: 'Translate assigned tags',
    defaultLabel: 'Tags',
    defaultPlaceholder: 'Add Tag',
  },
  modalTagTranslations: {
    title: 'Translate Photo Tags',
    saveButton: 'Save Translations',
    infoMsg: 'Translating a tag here applies anywhere it is in use (ex: other photos, posts, etc.)',
    propertyDisplayName: 'Photo Tag',
    successToast: 'Tag translations saved!',
  },
  translationFieldKeys: {
    name: 'Name',
    alt_text: 'Alt Text',
    description: 'Description',
    title: 'Title',
    summary: 'Summary',
    address: 'Address',
    city: 'City',
    province: 'Province',
    country: 'Country',
  },
  categories: {
    title: 'Manage Categories',
    addCategory: 'Add Category',
    newCategoryDefault: 'New Category',
    columnName: 'Name',
    columnEnabled: 'Enabled',
    columnActions: 'Actions',
    errorNamesEmpty: 'Category names can not be empty',
  },
  waitlist: {
    title: 'Waiting List Contact',
    tabs: {
      general: 'General',
    },
    fields: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      property: 'Property',
      city: 'City',
      bedrooms: 'Bedrooms',
      unitName: 'Unit Name',
      unitNumber: 'Unit Number',
      priceRange: 'Price Range',
      moveInDate: 'Move in Date',
      status: 'Status',
      notification: 'Notification',
      contactNotes: 'Contact Notes',
      site: 'Site',
      extraData: 'Extra Data',
      adminNotes: 'Admin Notes',
    },
    statusOptions: {
      selectStatus: 'Select a Status',
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    actions: {
      cancel: 'Cancel',
      save: 'Save',
      deleteRequest: 'Delete Request',
    },
    errors: {
      formInvalid: 'Form is invalid.',
    },
  },
  mediaProcessing: {
    metaImage: 'Processing meta image...',
    pdf: 'Processing PDF...',
    processing: 'Processing...',
    featureImage: 'Processing feature image...',
    uploadingTitle: 'Image uploading!',
    orgSize: 'Org. Size: ',
  },
} as const;

export const globalsFr = {
  actions: {
    addItem: 'Ajouter',
    addPanel: 'Ajouter un panneau',
    andMore: 'et {count} de plus',
    apply: 'Appliquer',
    cancel: 'Annuler',
    clear: 'Effacer',
    clearAll: 'Tout effacer',
    clearFilterSet: 'Effacer les filtres',
    clearFilters: 'Supprimer ({count}) Filtre',
    confirm: 'Confirmer',
    ok: 'OK',
    delete: 'Supprimer',
    deselectAll: 'Aucun',
    download: 'Télécharger',
    edit: 'Modifier',
    export: 'Exporter',
    exporting: 'Export en cours',
    exportToPdf: 'Exporter en PDF',
    help: 'Aide',
    learnMore: 'En savoir plus',
    moreInfo: 'Plus d\'information',
    preview: 'Aperçu',
    print: 'Imprimer',
    removeItem: 'Supprimer',
    reset: 'Réinitialiser',
    resetToDefault: 'Réinitialiser par défaut',
    save: 'Enregistrer',
    savePanels: 'Enregistrer les panneaux',
    search: 'Rechercher',
    selectAll: 'Tous',
    selected: '{count} sélectionné | {count} sélectionnés',
    translations: 'Traductions',
  },
  inquiryForm: {
    title: 'Demande d\'annonce mise en avant',
    close: 'Fermer',
    subtitle: 'Communiquez avec nous si vous avez besoin d\'aide ou de plus d\'information sur les annonces mises en avant ou les connexions.',
    // Identifiant du formulaire HubSpot (portail 4888868, région na1). Les libellés des
    // champs proviennent de la définition du formulaire dans HubSpot : la version
    // française est donc un formulaire distinct.
    formId: '0276fe71-08e1-40aa-8de6-7a9a5d08f37e',
  },
  calendar: {
    day: 'Jour',
    formatHint: 'Format : \'MM/JJ/AAAA\'',
    inputPlaceholder: 'MM/JJ/AAAA',
    month: 'Mois',
    months: {
      january: 'Janvier',
      february: 'Février',
      march: 'Mars',
      april: 'Avril',
      may: 'Mai',
      june: 'Juin',
      july: 'Juillet',
      august: 'Août',
      september: 'Septembre',
      october: 'Octobre',
      november: 'Novembre',
      december: 'Décembre',
    },
    next: 'Suivant',
    previous: 'Précédent',
    selectDate: 'Sélectionner une date',
    sync: 'Synchroniser',
    syncTooltip: 'Cela synchronisera votre calendrier',
    today: 'Aujourd\'hui',
    week: 'Semaine',
  },
  daysSelection: {
    title: 'Jours disponibles',
    initials: {
      sunday: 'D',
      monday: 'L',
      tuesday: 'M',
      wednesday: 'M',
      thursday: 'J',
      friday: 'V',
      saturday: 'S',
    },
  },
  gallery: {
    actions: {
      back: 'Retour',
      downloadFile: 'Télécharger le fichier',
      editPhoto: 'Modifier la photo',
      translateFields: 'Traduire les champs',
      uploadFiles: 'Téléverser les fichiers',
    },
    labels: {
      altText: 'Texte alternatif',
      current: 'Actuel',
      filename: 'Nom du fichier :',
      new: 'Nouveau',
      sourceHeight: 'Hauteur source :',
      sourceWidth: 'Largeur source :',
    },
    titles: {
      editImage: 'Modifier l\'image',
      gallery: 'Galerie',
      pagePhotos: 'Photos de la page',
      uploadFiles: 'Téléverser les fichiers',
    },
    tooltips: {
      disableAssign: 'Désactivez l\'image/le fichier/le média pour qu\'il ne s\'affiche plus sur les annonces et vos sites web connectés.',
      enableAssign: 'Activez l\'image/le fichier/le média pour qu\'il s\'affiche sur les annonces et vos sites web connectés.',
    },
    upload: {
      clickOrDrag: 'Cliquez ici ou glissez des éléments pour les téléverser',
      invalidFileType: 'Seules les images ou les fichiers PDF peuvent être téléversés !',
      uploadedFiles: 'Fichiers téléversés',
      uploading: 'Téléversement...',
    },
    warnings: {
      pageRemovalPendingSave: 'La suppression complète des photos ne sera effectuée qu\'une fois les modifications de la page enregistrées !',
      photoRemovalTitle: 'Avertissement de suppression de photo !',
    },
  },
  dataTable: {
    modalExport: {
      pickDateRange: 'Période à exporter',
    },
    exportCSV: 'Exporter en CSV',
    noResultsFound: 'Aucun résultat trouvé',
  },
  dates: {
    range: {
      from: 'Du',
      to: 'Au',
    },
    validation: {
      startDateRange: 'la date de début !',
      cannotBeBefore: 'ne peut pas être avant',
      endDateRange: 'La date de fin',
    },
    endDate: 'Date de fin',
    lastModified: 'Dernière modification',
    lastUpdated: 'Dernière mise à jour',
    today: 'Aujourd\'hui',
    na: 'S/O',
    startDate: 'Date de début',
  },
  dateRangePicker: {
    selectDate: 'Sélectionner une date',
    customRange: 'Plage personnalisée',
    lastDays: '{count} derniers jours',
    lastMonths: '{count} derniers mois',
    currentMonth: 'Mois en cours',
    previousMonth: 'Mois précédent',
  },
  fileInput: {
    browseButton: 'Parcourir',
    noFileChosen: 'Aucun fichier sélectionné',
    readableTypes: {
      image: 'images JPG ou PNG',
      pdf: 'fichiers PDF',
    },
    unsupportedFileType: 'Type de fichier non pris en charge. Veuillez télécharger uniquement {type}.',
  },
  filters: {
    advancedFiltersLocked: 'Les filtres avancés sont seulement disponibles avec l\'accès complet.',
    all: 'Tous',
    allFilters: 'Tous les filtres',
    applyFilters: 'Appliquer les filtres',
    cities: 'Villes',
    filterReports: 'Filtrer les rapports ({count})',
    filtersApplied: '[{count} filtres appliqué] | [{count} filtre appliqué] | [{count} filtres appliqués]',
    filtersAppliedCount: '({count} Filtre appliqué) | ({count} Filtres appliqués)',
    maxCount: '(Max {max})',
    multipleValueRequired: 'Au moins une sélection est requise pour le filtrage !',
    properties: 'Propriétés',
    resetFilters: 'Réinitialiser les filtres',
    selectCities: 'Sélectionner des villes',
    selectedCount: '[{count} sélectionné(s)]',
    selectProperties: 'Sélectionner des propriétés',
    selectPlaceholder: 'Sélectionner',
    selectStatus: 'Sélectionner un statut',
    showMore: 'AFFICHER PLUS',
    sidebarDefaultTitle: 'Filtres',
    singleValueRequired: 'Une valeur est requise pour le filtrage !',
    startEnd: 'Début et Fin',
    status: 'Statut',
  },
  languageNames: {
    french: 'Français',
    spanish: 'Espagnol',
    chineseSimplified: 'Chinois simplifié',
    chineseTraditional: 'Chinois traditionnel',
    fr: 'Français',
    es: 'Espagnol',
    zh: 'Chinois',
    de: 'Allemand',
    pt: 'Portugais',
    it: 'Italien',
    ja: 'Japonais',
    ko: 'Coréen',
    ar: 'Arabe',
    ru: 'Russe',
    zhs: 'Chinois simplifié',
    zht: 'Chinois traditionnel',
  },
  pagination: {
    of: 'sur',
    showing: 'Affichage',
  },
  panels: {
    addBuilding: 'Ajouter un bâtiment',
    currentFile: 'Actuellement :',
    currentLanguage: 'Langue actuelle du panneau :',
    noItemsInTranslateMode: 'Les éléments de panneau doivent d\'abord être créés en anglais, puis apparaîtront ici pour être traduits.',
    notDisplayedOnWebsite: 'Ce champ ne sera pas affiché sur votre site Web connecté.',
    panelLabel: 'Panneau',
    selectBuilding: 'Sélectionner un bâtiment :',
    selectPage: 'Sélectionner une page...',
    select: 'Sélectionner',
    translationsDisabledTooltip: 'Une valeur anglaise doit exister avant que ce panneau puisse être traduit. Veuillez enregistrer le panneau avant d\'ajouter des traductions.',
    errorNamesDifferent: 'Les noms de panneau doivent être différents',
    errorSavingContent: 'Erreur lors de l\'enregistrement du contenu personnalisé!',
    errorNeedsName: 'Les conteneurs de contenu personnalisé doivent au moins avoir un nom',
    errorSavingField: 'Erreur lors de l\'enregistrement de {groupName} - {fieldName}',
    errorFieldMissingValue: '{fieldName} n\'a pas sa valeur requise',
  },
  permissionsAccess: {
    noPermissionToMakeChanges: 'Vous n\'avez pas les autorisations nécessaires pour effectuer des modifications',
    viewOnly: 'Lecture seule',
  },
  permissionsBanners: {
    viewOnlyPage: 'Cette page est en mode consultation seulement. Vous n\'avez pas les autorisations requises pour la modifier. Contactez votre administrateur.',
  },
  seoCard: {
    current: 'Actuel :',
    defaultTitleUrlMessage: 'Par defaut, le titre de la page et l\'URL de la page seront crees a partir du titre {alertType}. Vous pouvez personnaliser chaque composant SEO en entrant des URLs personnalisees ci-dessous.',
    metaDescription: 'Meta description',
    metaDescriptionTooltip: 'La longueur optimale d\'une meta description est de 120 a 155 caracteres. Au-dela de cette limite, des mots pourraient etre tronques.',
    metaImage: 'Image meta',
    metaTitleTooltip: 'La longueur optimale d\'un titre meta est de 50 a 60 caracteres. Au-dela de cette limite, des mots pourraient etre tronques.',
    seoTitle: 'Titre SEO',
    seoUrl: 'URL SEO',
    strengthAverage: 'Moyen',
    strengthOptimal: 'Optimal',
    strengthTooLong: 'Trop long',
    strengthTooShort: 'Trop court',
    title: 'SEO',
  },
  status: {
    active: 'Actif',
    archived: 'Archivé',
    busy: 'Occupé',
    canceled: 'Annulé',
    completed: 'Complété',
    declined: 'Refusé',
    deleted: 'Supprimé',
    delivered: 'Livré',
    disabled: 'Désactivé',
    enabled: 'Actif',
    expired: 'Expiré',
    failed: 'Échoué',
    hidden: 'Masqué',
    inactive: 'Inactif',
    inProgress: 'En cours',
    ivrAnswered: 'Raccrochage RVI',
    ivrTimeout: 'Délai RVI',
    noAnswer: 'Aucune réponse',
    notPublished: 'Non publié',
    published: 'Publié',
    received: 'Reçu',
    requested: 'Demandé',
    resolved: 'Résolu',
    scheduled: 'Planifié',
    spam: 'Pourriel',
    undelivered: 'Non livré',
    workScheduled: 'Travaux planifiés',
  },
  tableTranslations: {
    searchPlaceholder: 'Rechercher par {property}...',
    enterTranslation: 'Saisir une traduction...',
    saveButton: 'Enregistrer les traductions',
    languageHeader: '{name} ({code})',
  },
  tipTap: {
    editContent: 'Modifier le Contenu',
  },
  toast: {
    successTitle: 'Succès !',
    successMessage: 'Modifications enregistrées !',
    errorTitle: 'Erreur !',
    errorMessage: 'Une erreur est survenue',
    genericError: 'Une erreur est survenue lors de cette requête. Veuillez réessayer.',
    warningTitle: 'Avertissement !',
    warningMessage: 'Veuillez noter',
    youHaveSomeErrors: 'Vous avez des erreurs ! Veuillez les corriger avant d\'enregistrer.',
    progressTitle: 'Enregistrement...',
    progressMessage: 'Les modifications sont en cours d\'enregistrement...',
  },
  translations: {
    title: 'Traductions',
    saveButton: 'Enregistrer les traductions',
    searchLabel: 'Rechercher',
    searchPlaceholder: 'Rechercher par nom de champ',
    unspecified: 'Non spécifié',
    enterTranslation: 'Saisir la traduction {lang}...',
    toastSaving: 'Enregistrement des traductions...',
    toastSavedTitle: 'Traductions Enregistrées!',
    toastSavedMessage: 'Vos traductions ont été enregistrées avec succès.',
  },
  units: {
    months: 'mois',
  },
  swal: {
    areYouSure: 'Êtes-vous sûr ?',
    areYouSureRemove: 'Êtes-vous sûr de vouloir supprimer ceci ?',
    continueWithoutSaving: 'Continuer sans enregistrer',
    delete: 'Supprimer',
    saveChanges: 'Enregistrer les modifications',
    success: 'Succès !',
    operationSuccessful: 'Opération réussie !',
  },
  unsavedChanges: {
    validTitle: 'Vous avez des modifications non enregistrées!',
    validText: 'Voulez-vous enregistrer vos modifications avant de quitter?',
    validConfirm: 'Enregistrer les modifications',
    invalidTitle: 'Vous avez des modifications non enregistrées contenant des erreurs.',
    invalidText: 'Vous devez corriger les erreurs avant d\'enregistrer. Voulez-vous quitter sans enregistrer?',
    invalidConfirm: 'Rester et corriger les erreurs',
    continueWithoutSaving: 'Continuer sans enregistrer',
  },
  validation: {
    alphaNumOnly: 'Veuillez utiliser uniquement des lettres ou des chiffres, sans espaces.',
    dateFormat: 'La date doit être au format JJ/MM/AAAA.',
    dateInvalid: 'Date invalide.',
    dateOutOfRange: 'La date est hors de la plage autorisée.',
    emailInvalid: 'Courriel invalide',
    emailsNotValid: 'Les adresses courriel ne sont pas valides.',
    embedCodeInvalid: 'La valeur n\'est pas un code d\'intégration valide.',
    fieldRequired: 'Ce champ est obligatoire',
    formInvalid: 'Le formulaire n\'est pas valide.',
    numericInvalid: 'Ce champ doit être numérique.',
    numericOnly: 'La saisie doit être numérique.',
    maxLength: 'Ce champ ne peut pas dépasser {max} caractères.',
    minLength: 'Ce champ doit contenir au moins {min} caractères.',
    maxValue: 'Ce champ doit être inférieur ou égal à {max}.',
    minValue: 'Ce champ doit être supérieur ou égal à {min}.',
    digits: 'Ce champ doit être numérique et contenir exactement {length} chiffres.',
    regexInvalid: 'Le format de ce champ est invalide.',
    phoneAltInvalid: 'Veuillez entrer un numéro de téléphone valide.',
    phoneInputInvalid: 'Le numéro de téléphone saisi n\'est pas valide',
    phoneIntlInvalid: 'Veuillez entrer un numéro de téléphone valide.',
    phoneNumberInvalid: 'Le numéro de téléphone n\'est pas valide.',
    phoneInvalid: 'Numéro de téléphone / télécopieur invalide.',
    postalInvalid: 'Le code postal n\'est pas valide.',
    rentMin: 'Le loyer doit être supérieur à 0',
    urlInvalid: 'L\'URL n\'est pas valide.',
    urlRegexInvalid: 'L\'URL du site web de la propriété n\'est pas valide.',
  },
  imageUploader: {
    promptUploadNewImage: 'Téléverser une image',
    supportedFileTypes: 'Types de fichiers supportés : png, gif, jpg',
  },
  buildingStackSync: {
    unitAvailabilityLocked: 'La modification de la disponibilité de l\'unité est désactivée par vos paramètres de synchronisation.',
    unitStatusLocked: 'La modification du statut de l\'unité est désactivée par vos paramètres de synchronisation.',
  },
  importer: {
    // `{'|'}` escapes the pipe: vue-i18n v9 reads a bare `|` as the plural-choice separator
    tooltip: 'ID d\'importation : {importId} {\'|\'} Source : {source}',
    tooltipNoSource: 'ID d\'importation : {importId}',
  },
  photoUpload: {
    deselectDisabledHint: 'Désélectionnez toutes les photos que vous souhaitez téléverser comme désactivées',
  },
  uploadProgress: {
    title: 'Téléversement en cours...',
    closeWarning: 'Veuillez ne pas fermer cette page jusqu\'à ce que le téléversement soit terminé',
    progressCount: 'Téléversement : {current} / {total}',
    percentComplete: '{percent} % terminé',
  },
  multipleInputs: {
    placeholderDefault: 'Ajouter une valeur',
    duplicateTagText: 'Valeur en double',
  },
  formTagsSelector: {
    tooltipHint: 'Appuyez sur la touche \'Entrée\' ou sur la virgule pour créer des étiquettes avant d\'enregistrer.',
    frequentlyUsedTags: 'Étiquettes fréquemment utilisées :',
    translateAssignedTags: 'Traduire les étiquettes assignées',
    defaultLabel: 'Étiquettes',
    defaultPlaceholder: 'Ajouter une étiquette',
  },
  modalTagTranslations: {
    title: 'Traduire les étiquettes de photo',
    saveButton: 'Enregistrer les traductions',
    infoMsg: 'Traduire une étiquette ici s\'applique partout où elle est utilisée (ex : autres photos, publications, etc.)',
    propertyDisplayName: 'Étiquette de photo',
    successToast: 'Traductions des étiquettes enregistrées !',
  },
  translationFieldKeys: {
    name: 'Nom',
    alt_text: 'Texte alternatif',
    description: 'Description',
    title: 'Titre',
    summary: 'Résumé',
    address: 'Adresse',
    city: 'Ville',
    province: 'Province',
    country: 'Pays',
  },
  categories: {
    title: 'Gérer les catégories',
    addCategory: 'Ajouter une catégorie',
    newCategoryDefault: 'Nouvelle catégorie',
    columnName: 'Nom',
    columnEnabled: 'Activé',
    columnActions: 'Actions',
    errorNamesEmpty: 'Les noms de catégorie ne peuvent pas être vides',
  },
  waitlist: {
    title: 'Contact de la liste d\'attente',
    tabs: {
      general: 'Général',
    },
    fields: {
      name: 'Nom',
      email: 'Courriel',
      phone: 'Téléphone',
      property: 'Propriété',
      city: 'Ville',
      bedrooms: 'Chambres',
      unitName: 'Nom de l\'unité',
      unitNumber: 'Numéro de l\'unité',
      priceRange: 'Fourchette de prix',
      moveInDate: 'Date d\'emménagement',
      status: 'Statut',
      notification: 'Notification',
      contactNotes: 'Notes de contact',
      site: 'Site',
      extraData: 'Données supplémentaires',
      adminNotes: 'Notes administrateur',
    },
    statusOptions: {
      selectStatus: 'Sélectionner un statut',
      enabled: 'Activé',
      disabled: 'Désactivé',
    },
    actions: {
      cancel: 'Annuler',
      save: 'Enregistrer',
      deleteRequest: 'Supprimer la demande',
    },
    errors: {
      formInvalid: 'Le formulaire est invalide.',
    },
  },
  mediaProcessing: {
    metaImage: 'Traitement de l\'image méta...',
    pdf: 'Traitement du PDF...',
    processing: 'Traitement...',
    featureImage: 'Traitement de l\'image principale...',
    uploadingTitle: 'Image en cours de téléversement!',
    orgSize: 'Taille org. : ',
  },
} as const;
